/* @flow */
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
import {trigger as edapTrigger} from './edapWrapper';
import {
    getEdapEventJson,
    triggerRecentlyViewedCarouselMabSelectedEvent
} from './collectionEdapUtils';

import type {ListingShape as MapHitListingShape} from '../components/MapHit/MapHit';
import type {ListingShape as HitListingShape} from '../components/Hit/Hit';
import type {BadgeShape as Badge} from '../components/Hit/Hit';
import type {SearchParamsType} from '../reducers/searchParams';

export const LIST_HIT_CLICK = 'search.hit.click';
export const MAP_HIT_CLICK = 'search.map.hit.click';
export const MAP_MARKER_CLICK = 'search.map.property.click';
export const SEARCH_RESULT_SET_SELECTED = 'search_result_set.selected';
export const SEARCH_RESULT_SET_MAB_SELECTED = 'multi_armed_bandit.selected';
export const SEARCH_RESULT_PRESENTED = 'search_result.presented';
export const DEEPEST_SEARCH_RESULT_PRESENTED =
    'deepest_search_result.presented';
export const VIRTUAL_TOUR_BADGE_EXPANDED =
    'virtualtour_badge_expansion.presented';

type HitEdapEventAction =
    | typeof LIST_HIT_CLICK
    | typeof MAP_HIT_CLICK
    | typeof MAP_MARKER_CLICK
    | typeof SEARCH_RESULT_SET_SELECTED;

type ListingShape = HitListingShape | MapHitListingShape;

export type HitEventDataListingShape = {
    listing: ListingShape,
    hitIndex?: number,
    page?: number,
    pageSize?: number
};

export type HitClickEventData = HitEventDataListingShape & {
    havId: string,
    deviceType: string,
    siteName: string,
    inputParams: SearchParamsType
};

export type MultiUnitPropertyDropDownShape = {
    propertyId: number
};

export type MultiUnitPropertyResultCountShape = {
    propertyId: number,
    resultCount: number
};

export type SearchResultPresentedEventData = HitEventDataListingShape & {
    timeOverListing: number,
    scrollVelocity: number
};

function parseBadges(listingBadges: ?(Badge[])) {
    let badges;
    if (listingBadges) {
        badges = listingBadges.map(({id}) => id);
    }

    return (badges || []).join(',');
}

export const handleBadgesNames = (badges: ?(Badge[])) => {
    let names;
    if (badges) {
        names = badges.map(({name}) => name);
    }
    return (names || []).join(',');
};

export const getRelativeAndAbsolutePositions = (
    hitIndex?: number,
    page?: number,
    pageSize?: number
) => {
    if (hitIndex || hitIndex === 0) {
        const relativePosition = hitIndex + 1;
        let absolutePosition;
        if (page && pageSize) {
            absolutePosition = (page - 1) * pageSize + hitIndex + 1;
        } else {
            absolutePosition = relativePosition;
        }

        return {
            absoluteposition: absolutePosition.toString(),
            relativeposition: relativePosition.toString()
        };
    }
    return {};
};

export const triggerHitEdapEvent = (
    eventData: HitEventDataListingShape,
    eventCategory: HitEdapEventAction,
    skipReportPositioning?: boolean,
    skipPriceSummaryTrigger?: boolean,
    isHighFrequency?: boolean
) => {
    const {
        listingId,
        rankedBadges,
        priceSummary,
        virtualTourBadge,
        geoDistance
    } = eventData.listing;
    const badges = [
        parseBadges(rankedBadges),
        virtualTourBadge && 'virtualTourBadge'
    ]
        .filter(Boolean)
        .join(',');
    const geodistancerelationtype = geoDistance
        ? geoDistance.relationType
        : null;
    let data = {
        eventlistingids: listingId,
        badges,
        geodistancerelationtype
    };

    if (!skipReportPositioning) {
        data = {
            ...data,
            ...getRelativeAndAbsolutePositions(
                eventData.hitIndex,
                eventData.page,
                eventData.pageSize
            )
        };
    }

    if (isHighFrequency) {
        data = {
            ...data,
            highfrequency: 'true'
        };
    }

    edapTrigger(eventCategory, data);

    if (priceSummary && !skipPriceSummaryTrigger) {
        edapTriggerPriceSummaryDisplay(priceSummary);
    }
};

export const triggerSearchResultSetSelectedEdapEvent = (
    eventData: HitClickEventData,
    eventCategory: HitEdapEventAction
) => {
    const {
        listing: {
            listingId,
            virtualTourBadge,
            rankedBadges,
            superlativesBadges,
            priceSummary
        },
        inputParams: {
            adultsCount,
            childrenCount,
            arrival,
            departure,
            petIncluded
        }
    } = eventData;
    const badges: string = [
        handleBadgesNames(superlativesBadges),
        handleBadgesNames(rankedBadges),
        virtualTourBadge && virtualTourBadge.name
    ]
        .filter(Boolean)
        .join(',');

    const {
        // eslint-disable-next-line camelcase
        price_accurate,
        // eslint-disable-next-line camelcase
        total_price: total_price_available
    } = getEdapEventJson(priceSummary);

    let data = {
        action_location: 'search',
        adult_count: adultsCount ? adultsCount.toString() : null,
        child_count: childrenCount ? childrenCount.toString() : null,
        listing_id: listingId,
        badges,
        date_end: departure,
        date_start: arrival,
        with_pets: String(petIncluded),
        price_accurate,
        total_price_available
    };

    edapTrigger(eventCategory, data);
};

export const triggerSearchResultSetSelectedMabEdapEvent = (
    eventData: HitClickEventData,
    eventCategory: HitEdapEventAction
) => {
    const {
        listing: {images},
        deviceType,
        havId,
        siteName
    } = eventData;
    const mab = images && images.length && images[0].mab;
    const arm = mab && mab.arm;
    if (!arm) {
        return;
    }

    let data = {
        action_location: 'search',
        bandit_id: mab && mab.banditId,
        campaign_id: mab && mab.campaignId,
        cached: mab && mab.cached,
        visitor_id: havId,
        arm_id: {
            categoryName: arm && arm.categoryName,
            level: arm && arm.level
        },
        context: {monikerName: siteName, device: deviceType},
        payload_id: mab && mab.payloadId,
        reward_id: 0
    };

    edapTrigger(eventCategory, data);
};

export const triggerListHitClickEDAPEvent = (eventData: HitClickEventData) => {
    const {
        listing,
        hitIndex,
        page,
        pageSize,
        inputParams,
        havId,
        deviceType,
        siteName
    } = eventData;
    triggerHitEdapEvent(
        {listing, hitIndex, page, pageSize},
        LIST_HIT_CLICK,
        false,
        false
    );
    triggerSearchResultSetSelectedEdapEvent(
        {listing, inputParams, havId, deviceType, siteName},
        SEARCH_RESULT_SET_SELECTED
    );
    triggerSearchResultSetSelectedMabEdapEvent(
        {listing, inputParams, havId, deviceType, siteName},
        SEARCH_RESULT_SET_MAB_SELECTED
    );
    triggerRecentlyViewedCarouselMabSelectedEvent();
};

export const triggerMapHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_HIT_CLICK, false, false);
};

export const triggerMarkerClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_MARKER_CLICK, false, true);
};

export const triggerExpandedGroupHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, LIST_HIT_CLICK, true, false);
};

export const isInViewPort = (hitElement: Element) => {
    const bottomBoundry = window.innerHeight;
    const hitBoundingObj = hitElement.getBoundingClientRect();

    return (
        hitBoundingObj.top > (3 * hitBoundingObj.height) / 4 &&
        hitBoundingObj.bottom - hitBoundingObj.height / 4 < bottomBoundry
    );
};

export const getOtherVisibleListings = (hitIndex?: number) => {
    const listingsIndices = [];
    const hitElements = document.querySelectorAll('.Results .Hit');

    for (let i = 0; i < hitElements.length; i++) {
        const listingIndex = +hitElements[i].getAttribute('hit-index');
        if (hitIndex !== listingIndex && isInViewPort(hitElements[i])) {
            listingsIndices.push(listingIndex + 1);
        }
    }

    return listingsIndices.join(',');
};

export let deepestSearchResult = {relativeposition: '-1'};

export const triggerDeepestSearchResultPresentedEDAPEvent = () => {
    if (deepestSearchResult.relativeposition !== '-1') {
        edapTrigger(DEEPEST_SEARCH_RESULT_PRESENTED, {...deepestSearchResult});
        deepestSearchResult.relativeposition = '-1';
    }
};

export const triggerSearchResultPresentedEDAPEvent = (
    eventData: SearchResultPresentedEventData
) => {
    const {rankedBadges, virtualTourBadge, priceSummary} = eventData.listing;

    const badges: string = [
        handleBadgesNames(rankedBadges),
        virtualTourBadge && virtualTourBadge.name
    ]
        .filter(Boolean)
        .join(',');

    let data = {
        badges,
        scroll_velocity: Math.abs(Math.floor(eventData.scrollVelocity)),
        time_over_listing: eventData.timeOverListing,
        scroll_direction: eventData.scrollVelocity < 0 ? 'up' : 'down',
        ...getRelativeAndAbsolutePositions(
            eventData.hitIndex,
            eventData.page,
            eventData.pageSize
        ),
        other_visible_results: getOtherVisibleListings(eventData.hitIndex)
    };
    if (
        Number(deepestSearchResult.relativeposition) <
        Number(data.relativeposition)
    ) {
        deepestSearchResult = {
            scroll_velocity: data.scroll_velocity,
            relativeposition: data.relativeposition,
            absoluteposition: data.absoluteposition,
            time_over_listing: data.time_over_listing,
            other_visible_results: data.other_visible_results,
            scroll_direction: data.scroll_direction
        };
    }

    if (priceSummary && priceSummary.formattedAmount) {
        data = {...data, stramount: priceSummary.formattedAmount};
    }

    edapTrigger(SEARCH_RESULT_PRESENTED, data);
};
